import React from "react";
import { useNavigate } from "react-router-dom";
import { User } from "hooks/useUsers";
import { SkeletonCard } from "components/shared/SkeletonCard";
import {
  StatusBadge,
  RoleBadge,
  UserCard,
  UserCardHeader,
  UserCardTitle,
  UserCardContent,
  UserCardInfo,
  UserCardInfoRow,
  UserCardLabel,
  UserCardValue,
  UserCardBadges,
  UsersGrid,
  DeviceLink,
} from "styles/components/usersList.styles";
import { buildDeviceDetailsPath } from "utils/deviceNavigation";

interface UsersCardViewProps {
  users: User[];
  isLoading: boolean;
}

const UsersCardView: React.FC<UsersCardViewProps> = ({ users, isLoading }) => {
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDeviceClick = (serialNumber: string) => {
    navigate(buildDeviceDetailsPath({ serialNumber, isAdminView: true }));
  };

  const handleUserClick = (userId: number) => {
    navigate(`/users/${userId}`);
  };

  if (isLoading) {
    return (
      <UsersGrid role="status" aria-busy="true" aria-label="Loading users">
        {Array.from({ length: 4 }, (_, index) => (
          <SkeletonCard key={index} />
        ))}
      </UsersGrid>
    );
  }

  return (
    <UsersGrid>
      {users.map((user: User) => (
        <UserCard key={user.id} onClick={() => handleUserClick(user.id)}>
          <UserCardHeader>
            <UserCardTitle>
              {user.firstname} {user.lastname}
            </UserCardTitle>
            <UserCardBadges>
              <RoleBadge role={user.privledgelevel}>
                {user.privledgelevel?.toLowerCase() || "user"}
              </RoleBadge>
            </UserCardBadges>
          </UserCardHeader>

          <UserCardContent>
            <UserCardInfo>
              <UserCardInfoRow>
                <UserCardLabel>Email:</UserCardLabel>
                <UserCardValue>{user.email}</UserCardValue>
              </UserCardInfoRow>
              <UserCardInfoRow>
                <UserCardLabel>Device:</UserCardLabel>
                {user.Device?.serialNumber ? (
                  <DeviceLink
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeviceClick(user.Device!.serialNumber);
                    }}
                  >
                    {user.Device.serialNumber}
                  </DeviceLink>
                ) : (
                  <UserCardValue>-</UserCardValue>
                )}
              </UserCardInfoRow>
              <UserCardInfoRow>
                <UserCardLabel>Verified:</UserCardLabel>
                <StatusBadge status={user.isVerified ? "active" : "inactive"}>
                  {user.isVerified ? "Verified" : "Unverified"}
                </StatusBadge>
              </UserCardInfoRow>
              <UserCardInfoRow>
                <UserCardLabel>Status:</UserCardLabel>
                <StatusBadge status={user.isdisabled ? "disabled" : "active"}>
                  {user.isdisabled ? "Disabled" : "Active"}
                </StatusBadge>
              </UserCardInfoRow>
              <UserCardInfoRow>
                <UserCardLabel>Updated:</UserCardLabel>
                <UserCardValue>{formatDate(user.updatedAt)}</UserCardValue>
              </UserCardInfoRow>
            </UserCardInfo>
          </UserCardContent>
        </UserCard>
      ))}
    </UsersGrid>
  );
};

export default UsersCardView;
